import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type {
  AlertRule,
  Instance,
  Metrics,
  PowerAction,
  TrafficPackage,
} from '@neburst-bot/shared';
import { api, instancesStreamUrl } from './client';
import { getInitData } from '../lib/tg';

// ===== Auth =====

export interface MeResponse {
  loggedIn: boolean;
  email?: string | null;
  nickname?: string | null;
  neburstUserUuid?: string | null;
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/auth/me'),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<{ ok: boolean; requires2fa?: boolean; methods?: string[]; error?: string }>(
        '/auth/login',
        body,
      ),
    onSuccess: (data) => {
      if (data.ok && !data.requires2fa) qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useTotp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string }) =>
      api.post<{ ok: boolean; error?: string }>('/auth/totp', body),
    onSuccess: (data) => {
      if (data.ok) qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/auth/logout'),
    onSuccess: () => {
      qc.clear();
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

// ===== Instances =====

export function useInstancesStream() {
  const [instances, setInstances] = useState<Map<string, Instance>>(new Map());
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const ac = new AbortController();
    setError(null);
    setInstances(new Map());
    (async () => {
      try {
        const res = await fetch(instancesStreamUrl(), {
          headers: { Authorization: `tma ${getInitData()}` },
          signal: ac.signal,
        });
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const events = buf.split('\n\n');
          buf = events.pop() ?? '';
          for (const evt of events) {
            const dataLine = evt.split('\n').find((l) => l.startsWith('data: '));
            if (!dataLine) continue;
            try {
              const inst = JSON.parse(dataLine.slice(6)) as Instance;
              setInstances((m) => {
                const next = new Map(m);
                next.set(inst.uuid, inst);
                return next;
              });
            } catch {
              // skip
            }
          }
        }
      } catch (e) {
        if ((e as { name?: string }).name !== 'AbortError') setError(String(e));
      }
    })();
    return () => ac.abort();
  }, []);
  return { instances: [...instances.values()], error };
}

export function useInstance(uuid: string) {
  return useQuery({
    queryKey: ['instance', uuid],
    queryFn: () => api.get<Instance>(`/instances/${uuid}`),
  });
}

export function useInstanceMetrics(uuid: string) {
  return useQuery({
    queryKey: ['metrics', uuid],
    queryFn: () => api.get<Metrics>(`/instances/${uuid}/metrics`),
    refetchInterval: 10_000,
    staleTime: 0,
  });
}

export function useTrafficPackages(uuid: string) {
  return useQuery({
    queryKey: ['traffic', uuid],
    queryFn: () => api.get<{ packages: TrafficPackage[] }>(`/instances/${uuid}/traffic_packages`),
  });
}

export interface MetricsHistory {
  cpu?: { x: string[]; y: number[] };
  memory?: { x: string[]; y: number[] };
  traffic?: { rx?: { x: string[]; y: number[] }; tx?: { x: string[]; y: number[] } };
  /** traffic_daily.rx/tx are ALSO {x, y} — x is ISO day, y is GB. */
  traffic_daily?: { rx?: { x: string[]; y: number[] }; tx?: { x: string[]; y: number[] } };
  disk?: { r?: { x: string[]; y: number[] }; w?: { x: string[]; y: number[] } };
}

export function useMetricsHistory(uuid: string, hours = 6) {
  return useQuery({
    queryKey: ['metricsHistory', uuid, hours],
    queryFn: () => {
      const end = new Date();
      const start = new Date(end.getTime() - hours * 3600_000);
      const q = `?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`;
      return api.get<MetricsHistory>(`/instances/${uuid}/metrics/history${q}`);
    },
    staleTime: 60_000,
  });
}

export interface InstanceShape {
  code?: string;
  display?: string;
  detail?: {
    cpu_cores?: number;
    memory?: { amount?: number; unit?: string };
    storage?: { amount?: number; unit?: string };
    network_speed?: { amount?: number; unit?: string };
  };
}
export function useInstanceShape(uuid: string) {
  return useQuery({
    queryKey: ['shape', uuid],
    queryFn: () => api.get<InstanceShape>(`/instances/${uuid}/shape`),
    staleTime: 5 * 60_000,
  });
}

export interface InstanceBilling {
  pay_cycle?: string;
  renew?: { date?: string; price?: number; auto_renew_enabled?: boolean };
  pay_type?: { code?: string };
  billing_stopped?: boolean;
}
export function useInstanceBilling(uuid: string) {
  return useQuery({
    queryKey: ['instanceBilling', uuid],
    queryFn: () => api.get<InstanceBilling>(`/instances/${uuid}/billing`),
    staleTime: 60_000,
  });
}

export function useInstanceAction(uuid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: PowerAction) =>
      api.post<{ ok: true }>(`/instances/${uuid}/action`, { action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instance', uuid] }),
  });
}

// ===== Billing =====

export interface BalanceData {
  data: Record<string, unknown> | null;
}
export function useBalance() {
  return useQuery({
    queryKey: ['balance'],
    queryFn: () => api.get<BalanceData>('/billing/balance'),
    refetchInterval: 60_000,
  });
}

export function useInvoices() {
  return useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.get<{ data: unknown }>('/billing/invoices'),
  });
}

export function useInvoice(uuid: string) {
  return useQuery({
    queryKey: ['invoice', uuid],
    queryFn: () => api.get<Record<string, unknown>>(`/billing/invoices/${uuid}`),
  });
}

// ===== Notifications =====

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ data?: { list?: Record<string, unknown>[] } } | Record<string, unknown>>('/notifications'),
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.get<Record<string, unknown>>('/notifications/unread-count'),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => api.post(`/notifications/read/${uuid}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// ===== Tickets =====

export function useTickets() {
  return useQuery({
    queryKey: ['tickets'],
    queryFn: () => api.get<Record<string, unknown>>('/tickets'),
  });
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.get<Record<string, unknown>>(`/tickets/${id}`),
    refetchInterval: 10_000,
  });
}

export function useNewTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; content: string; category?: string }) =>
      api.post<Record<string, unknown>>('/tickets/new', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}

export function useReplyTicket(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      api.post<Record<string, unknown>>(`/tickets/${id}/reply`, { content }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket', id] }),
  });
}

// ===== Profile =====

export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get<{ data: Record<string, unknown> | null }>('/profile'),
  });
}

export function useUpdateNickname() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nickname: string) => api.post('/profile/nickname', { nickname }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useUpdateLanguage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (language: string) => api.post('/profile/language', { language }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  });
}

// ===== Affiliate =====

export function useAffiliateOverview() {
  return useQuery({
    queryKey: ['affiliate'],
    queryFn: () => api.get<Record<string, unknown>>('/affiliate/overview'),
  });
}

// ===== Alerts =====

export function useAlerts() {
  return useQuery({
    queryKey: ['alerts'],
    queryFn: () => api.get<{ rules: AlertRule[] }>('/alerts'),
  });
}

export function useAddAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api.post<{ id: number }>('/alerts', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  });
}

export function useDeleteAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<{ ok: true }>(`/alerts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  });
}
